<template>
  <!-- The Credit / Refund tab of the customer page: their Stripe credit balance,
       adjusting it or refunding its card money to the card, and refunding or
       crediting an invoice. -->
  <v-card flat>
    <customer-refund-dialog v-model="showRefund" :uid="uid" @refunded="changed"/>

    <v-dialog v-model="showAdjust" max-width="520px" persistent>
      <v-card>
        <v-card-title>Adjust or refund credit balance</v-card-title>
        <v-card-text>
          <v-alert v-if="error" type="error" dense outlined>{{ error }}</v-alert>
          <v-radio-group v-model="adjust.direction" row dense>
            <v-radio label="Add credit" value="add"/>
            <v-radio label="Take credit away" value="remove"/>
            <v-radio label="Refund to the card" value="refund" :disabled="!refundableCents" data-cy="refund-balance-option"/>
          </v-radio-group>
          <v-text-field v-model="adjust.amount" label="Amount ($)" type="number" min="0" step="0.01" prefix="$" autofocus
                        :error-messages="refundTooMuch ? `At most ${formatCurrency(refundableCents)} is refundable to the card` : []"
                        :hint="isRefund ? `Up to ${formatCurrency(refundableCents)} of card payments goes back to the card it came from` : ''"
                        persistent-hint/>
          <v-textarea v-model="adjust.note" label="Note (why, required)" rows="2" auto-grow
                      :hint="isRefund ? 'Saved on the Stripe refund and as a CS note on the customer' : 'Saved on the Stripe transaction and as a CS note on the customer'"
                      persistent-hint/>
          <v-row v-if="info && info.customer" dense class="mt-3" data-cy="balance-preview">
            <v-col cols="6">
              <div class="caption text--secondary">Current balance</div>
              <div class="subtitle-1" :class="balanceClass(info.customer.balance)">{{ balanceTextOf(info.customer.balance) }}</div>
            </v-col>
            <v-col cols="6">
              <div class="caption text--secondary">After this adjustment</div>
              <div class="text-h6" :class="balanceClass(adjustedBalance)">{{ balanceTextOf(adjustedBalance) }}</div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text :disabled="adjusting" @click="showAdjust = false">Cancel</v-btn>
          <v-btn color="primary" :loading="adjusting" :disabled="!adjustCents || !adjust.note.trim() || refundTooMuch"
                 data-cy="adjust-save" @click="adjustBalance">
            {{ isRefund ? 'Refund' : 'Save' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-card-text>
      <v-alert v-if="error" type="error" dense outlined dismissible @input="error = null">{{ error }}</v-alert>
      <v-alert v-if="balanceRefund" :type="balanceRefund.error ? 'warning' : 'success'" dense outlined dismissible
               data-cy="balance-refund-result" @input="balanceRefund = null">
        Refunded {{ formatCurrency(balanceRefund.refunded) }} to the card:
        <span v-for="(refund, index) in balanceRefund.refunds" :key="refund.refund_id">
          <span v-if="index">, </span><strong>{{ formatCurrency(refund.amount) }} to {{ refund.card_text }}</strong> (week {{ refund.week_date }})
        </span>.
        Tell the customer which card it went to; it shows on their statement in 5 to 10 business days.
        <div v-if="balanceRefund.error">{{ balanceRefund.error }}</div>
      </v-alert>
      <v-progress-linear v-if="loading" indeterminate/>

      <template v-if="info">
        <v-alert v-if="!info.stripe_id" type="warning" dense outlined>
          This customer has no Stripe customer.
        </v-alert>
        <v-alert v-else-if="!info.customer" type="warning" dense outlined>
          Stripe customer <code>{{ info.stripe_id }}</code> was not found on the current Stripe account.
        </v-alert>

        <v-card v-if="info.customer" outlined>
          <v-card-title class="subtitle-1">
            Credit balance
            <v-spacer/>
            <v-btn small outlined color="primary" class="mr-2" :disabled="!canEditCouponsAndCredit" @click="openAdjust">
              <v-icon left small>mdi-cash-plus</v-icon>
              Adjust/refund balance
            </v-btn>
            <v-btn small outlined color="error" class="mr-2" :disabled="!canEditCouponsAndCredit" @click="showRefund = true">
              <v-icon left small>mdi-cash-refund</v-icon>
              Refund or credit an invoice
            </v-btn>
            <v-btn small outlined :href="`https://dashboard.stripe.com/${newStripeAccount}/customers/${info.stripe_id}`" target="_blank">
              <v-icon left small>mdi-open-in-new</v-icon>
              Stripe
            </v-btn>
          </v-card-title>
          <v-card-text>
            <div class="text-h6" :class="balanceClass(info.customer.balance)">
              {{ balanceTextOf(info.customer.balance) }}
            </div>
            <div class="caption text--secondary">
              Stripe draws credit down on the customer's next invoices. Gift cards redeemed through Fed become credit.
            </div>
            <div v-if="info.customer.balance < 0" class="mt-4" data-cy="refundable-credit">
              <v-progress-linear v-if="refundableLoading" indeterminate/>
              <div v-else-if="refundableError" class="caption error--text">{{ refundableError }}</div>
              <template v-else-if="refundable">
                <div class="subtitle-1">
                  Refundable to the card: <strong>{{ formatCurrency(refundable.refundable) }}</strong>
                </div>
                <div class="caption text--secondary">
                  Card payments that order changes gave back as credit and that are still on the balance
                  (credit is counted as spent from card money first). Gift card, referral and kitchen credit is not refundable.
                </div>
                <v-simple-table v-if="refundable.sources.length" dense class="mt-2" data-cy="refundable-sources">
                  <thead>
                  <tr>
                    <th>Week</th>
                    <th>Invoice</th>
                    <th>Credit note</th>
                    <th class="text-right">Credited</th>
                    <th class="text-right">From the card</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="source in refundable.sources" :key="source.credit_note_id">
                    <td>{{ source.week_date }}</td>
                    <td><code>{{ source.invoice_id }}</code></td>
                    <td><code>{{ source.credit_note_id }}</code> {{ dateFormatted(source.created_at, {formatString: 'll'}) }}</td>
                    <td class="text-right">{{ formatCurrency(source.credited) }}</td>
                    <td class="text-right">{{ formatCurrency(source.card_amount) }}</td>
                  </tr>
                  </tbody>
                </v-simple-table>
              </template>
            </div>
          </v-card-text>
        </v-card>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import api from '@/api';
import CustomerRefundDialog from '@/components/CustomerRefundDialog';
import {dateFormatted, formatCurrency} from '@/store/utils';

const NEW_STRIPE_ACCOUNT = 'acct_1GjcI1D0KVemEdeG';

export default {
  name: 'CustomerCreditRefund',
  components: {CustomerRefundDialog},
  props: {
    uid: {type: String, required: true},
    // these buttons change what the customer pays
    canEditCouponsAndCredit: {type: Boolean, default: false},
  },
  data() {
    return {
      newStripeAccount: NEW_STRIPE_ACCOUNT,
      info: null,
      loading: false,
      error: null,
      showAdjust: false,
      adjusting: false,
      adjust: {direction: 'add', amount: '', note: ''},
      showRefund: false,
      // the part of the credit that is card money (GET refundable-credit)
      refundable: null,
      refundableLoading: false,
      refundableError: null,
      // the result of the last refund to the card, kept on the tab to tell the customer
      balanceRefund: null,
    };
  },
  computed: {
    isRefund() {
      return this.adjust.direction === 'refund';
    },
    refundableCents() {
      return (this.refundable && this.refundable.refundable) || 0;
    },
    // the credit the dialog adds, in cents (negative when it takes credit away or refunds it)
    adjustCents() {
      const cents = Math.round(Number(this.adjust.amount) * 100);
      if (!(cents > 0)) return 0;
      return this.adjust.direction === 'add' ? cents : -cents;
    },
    refundTooMuch() {
      return this.isRefund && -this.adjustCents > this.refundableCents;
    },
    // the customer's Stripe balance once the adjustment is saved (negative is credit)
    adjustedBalance() {
      return this.info.customer.balance - this.adjustCents;
    },
  },
  watch: {
    uid() {
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    dateFormatted,
    formatCurrency,

    async load() {
      this.loading = true;
      this.error = null;
      try {
        const {data} = await api.get(`v2/stripe/coupons/customer/${this.uid}`);
        this.info = data;
      } catch (e) {
        this.error = this.messageOf(e, 'Failed to load the customer\'s credit');
      } finally {
        this.loading = false;
      }
      this.loadRefundable();
    },

    async loadRefundable() {
      this.refundable = null;
      this.refundableError = null;
      if (!(this.info && this.info.customer && this.info.customer.balance < 0)) return;
      this.refundableLoading = true;
      try {
        const {data} = await api.get(`v2/stripe/coupons/customer/${this.uid}/refundable-credit`);
        this.refundable = data;
      } catch (e) {
        this.refundableError = this.messageOf(e, 'Could not work out how much of the credit is refundable');
      } finally {
        this.refundableLoading = false;
      }
    },

    balanceTextOf(balance) {
      if (balance < 0) return `${formatCurrency(-balance)} credit`;
      if (balance > 0) return `${formatCurrency(balance)} owed`;
      return 'No credit';
    },

    balanceClass(balance) {
      if (balance < 0) return 'green--text';
      if (balance > 0) return 'red--text';
      return '';
    },

    openAdjust() {
      this.error = null;
      this.adjust = {direction: 'add', amount: '', note: ''};
      this.showAdjust = true;
    },

    async adjustBalance() {
      if (this.isRefund) return this.refundBalance();
      this.adjusting = true;
      try {
        const {data} = await api.post(`v2/stripe/coupons/customer/${this.uid}/balance`, {
          credit: this.adjustCents,
          note: this.adjust.note.trim(),
        });
        this.info = data;
        this.loadRefundable();
        this.showAdjust = false;
        this.$emit('changed');
        this.$store.commit('showSnackbar', {message: 'Credit balance adjusted', color: 'success'});
      } catch (e) {
        this.error = this.messageOf(e, 'Failed to adjust the credit balance');
      } finally {
        this.adjusting = false;
      }
    },

    async refundBalance() {
      this.adjusting = true;
      this.error = null;
      try {
        const {data} = await api.post(`v2/stripe/coupons/customer/${this.uid}/refund-balance`, {
          amount: -this.adjustCents,
          note: this.adjust.note.trim(),
        });
        const {refunded, refunds, error, ...info} = data;
        this.info = info;
        this.balanceRefund = {refunded, refunds, error};
        this.loadRefundable();
        this.showAdjust = false;
        this.$emit('changed');
        const cards = [...new Set(refunds.map(refund => refund.card_text))].join(' and ');
        this.$store.commit('showSnackbar', {message: `Refunded ${formatCurrency(refunded)} to ${cards}`, color: error ? 'warning' : 'success'});
      } catch (e) {
        this.error = this.messageOf(e, 'Failed to refund the credit to the card');
      } finally {
        this.adjusting = false;
      }
    },

    // a credit note can add to the balance, and writes a CS note
    changed() {
      this.load();
      this.$emit('changed');
    },

    messageOf(e, fallback) {
      return (e.response && e.response.data && (e.response.data.message || e.response.data.error)) || e.message || fallback;
    },
  },
};
</script>
